import axios from 'axios'
import { apiNoVersionBase } from 'config/env'

import * as snugLogger from 'app/services/snugLogger'

/**
 * V2 API client for offer attributes
 * Uses apiNoVersionBase to avoid /v1/ prefix that's added by the standard api helper
 */
const v2Api = axios.create({
  baseURL: `${apiNoVersionBase}/`,
  withCredentials: true,
})

/**
 * Offer Attributes API Service
 * Handles SA Form A1 fields for the Disclosures tab
 */

export interface LeaseDisclosures {
  exclusions?: string
  landlord_intends_to_sell?: boolean | null
  // Leasing Rates & Charges - v3 fields
  water_responsibility?: 'tenant' | 'landlord' | null
  water_details?: string
  electricity_responsibility?: 'tenant' | 'landlord' | null
  gas_responsibility?: 'tenant' | 'landlord' | 'not_applicable' | null
  other_charges?: string
}

export interface LeaseDisclosuresPublishAuditEntry {
  published_at: string
  published_by_id: number
  published_by_name: string
}

export interface LeaseDisclosuresAudit {
  published?: LeaseDisclosuresPublishAuditEntry[]
}

export interface OfferAttributes {
  lease_disclosures?: LeaseDisclosures
  lease_disclosures_audit?: LeaseDisclosuresAudit
}

export interface OfferAttributesResponse {
  offer_guid: string
  attributes: {
    lease_disclosures: LeaseDisclosures
    lease_disclosures_audit?: LeaseDisclosuresAudit
  }
  version: number
}

export interface V2ApiResponse<T> {
  data: T
  meta?: {
    timestamp: string
    version: string
    execution_time_ms: number
  }
}

export interface ApiError {
  message?: string
  response?: {
    status: number
    data?: unknown
  }
  errorCode?: string
}

/**
 * Fetches offer attributes for a given offer.
 * Returns empty object if attributes don't exist yet (404).
 */
export const fetchOfferAttributes = async (
  teamGuid: string,
  offerGuid: string,
): Promise<OfferAttributes> => {
  try {
    const response = await v2Api.get<V2ApiResponse<OfferAttributesResponse>>(
      `v2/teams/${teamGuid}/offers/${offerGuid}/attributes`,
    )
    return response.data.data.attributes || { lease_disclosures: {} }
  } catch (error) {
    const apiError = error as ApiError
    if (apiError.response?.status === 404) {
      return { lease_disclosures: {} } // Not found is expected - return empty object for new attributes
    }
    snugLogger.error('Error fetching offer attributes', { error })
    throw new Error(apiError.message || 'Failed to fetch offer attributes')
  }
}

/**
 * Updates offer attributes (upsert).
 */
export const updateOfferAttributes = async (
  teamGuid: string,
  offerGuid: string,
  leaseDisclosures: Partial<LeaseDisclosures>,
): Promise<OfferAttributes> => {
  try {
    const response = await v2Api.put<V2ApiResponse<OfferAttributesResponse>>(
      `v2/teams/${teamGuid}/offers/${offerGuid}/attributes`,
      { lease_disclosures: leaseDisclosures },
    )
    return response.data.data.attributes || { lease_disclosures: {} }
  } catch (error) {
    const apiError = error as ApiError
    if (apiError.response?.status === 400) {
      throw new Error('Invalid offer attributes. Please check your inputs.')
    } else if (apiError.response?.status === 404) {
      throw new Error('Offer not found.')
    }
    snugLogger.error('Error updating offer attributes', { error })
    throw new Error(apiError.message || 'Failed to update offer attributes')
  }
}
